import axios from 'axios';
import jwt_decode from 'jwt-decode';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put } from 'redux-saga/effects';

import { saveloginData } from './index';
import { setUserDetails } from '../Settings/index';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import { GTM_TRACK_MODE } from '../config';
import { trackUserSignup } from '../../utils/trackingHelper';

/*========== LOGIN FUNCTIONS =============*/

function* login({ payload, history }) {
  yield put({ type: 'TOGGLE_LOGIN' });
  const { error, response } = yield call(postCall, { path: '/user/auth', payload });
  if (error) {
    // Check if error is due to wrong platform (403 status)
    if (error?.response?.status === 403 && error?.response?.data?.body?.redirectUrl) {
      const redirectUrl = error.response.data.body.redirectUrl;
      const message = error.response.data.message || 'Redirecting to correct platform...';

      EventBus.publish('error', message);

      // Wait 2 seconds then redirect to correct platform
      yield put({ type: 'TOGGLE_LOGIN' });
      setTimeout(() => { window.location.href = redirectUrl }, 2000);
      return;
    }
    const errorMessage = error['response']['data']['message'];
    const isUserStatusError = errorMessage && ( errorMessage.includes('account has been paused') || errorMessage.includes('account has been deactivated'));
    if (isUserStatusError) EventBus.publish('error:403', errorMessage);
    else EventBus.publish('error', errorMessage);
    
  }
  else if (response) {
    const responseData = response['data']['body'];

    // Check if 2FA verification is required
    if (responseData['require2FA']) {
      yield put({ type: 'TOGGLE_LOGIN' });
      yield put({ type: 'SET_2FA_REQUIRED', payload: responseData });
      // The UI will handle showing the OTP input screen
      return;
    }

    // Normal login flow
    yield put(saveloginData(responseData));
    yield put({ type: 'GET_ME' });

    // Track login event in GTM with tracking_id
    // NOTE: After login, user object from backend contains tracking_id (saved during signup)
    if (typeof window !== 'undefined' && window.dataLayer) {
      const eventData = {
        event: 'user_login',
        user_id: responseData?.user?._id,
        user_email: responseData?.user?.email,
        login_method: 'email',
        tracking_id: responseData?.user?.tracking_id || 'none',
        acquisition_source: responseData?.user?.acquisition_source || 'direct'
      };

      // Check tracking mode: 0=all users, 1=only tracking_id users
      if (GTM_TRACK_MODE === 0 || (GTM_TRACK_MODE === 1 && eventData.tracking_id && eventData.tracking_id !== 'none')) {
        window.dataLayer.push(eventData);
      }
    }

    const workspaceId = responseData['user']?.['activeWorkspace'];
    setTimeout(() => history.push(getWorkspacePath('/agents', workspaceId)), 100);
  }
  yield put({ type: 'TOGGLE_LOGIN' });
}

function* magicLogin({ payload }) {
  const { hash, history } = payload;
  yield put({ type: 'TOGGLE_LOGIN' });
  const { error, response } = yield call(getCall, `/user/magicLogin/${hash}`);
  if (error) {
    // Check if error is due to wrong platform (403 status)
    if (error?.response?.status === 403 && error?.response?.data?.body?.redirectUrl) {
      const redirectUrl = error.response.data.body.redirectUrl;
      const message = error.response.data.message || 'Redirecting to correct platform...';

      EventBus.publish('error', message);

      // Wait 2 seconds then redirect to correct platform
      yield put({ type: 'TOGGLE_LOGIN' });
      setTimeout(() => { window.location.href = redirectUrl }, 2000);
      return;
    }

    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Invalid or expired login link');
    yield put({ type: 'TOGGLE_LOGIN' });
    setTimeout(() => history.push('/login'), 1000);
  } else if (response) {
    const responseData = response['data']['body'];

    // Check if user needs to set up password - SECURITY: Keep them on login screen
    if (responseData['requiresPasswordSetup']) {
      // Save login data but set requiresPasswordSetup flag
      yield put(saveloginData(responseData));
      yield put({ type: 'SET_REQUIRES_PASSWORD_SETUP', payload: true });
      // IMPORTANT: Fetch user details so password modal can detect temp_password
      yield put({ type: 'GET_ME' });
      yield put({ type: 'TOGGLE_LOGIN' });
      // Stay on login page - do NOT redirect to agents
      setTimeout(() => history.push('/login'));
      return;
    }

    // Normal magic login flow - redirect to agents
    yield put(saveloginData(responseData));
    yield put({ type: 'GET_ME' });
    const workspaceId = responseData['currentWorkspace'] || responseData['user']?.['currentWorkspace'];

    yield put({ type: 'TOGGLE_LOGIN' });
    setTimeout(() => history.push(getWorkspacePath('/agents', workspaceId)), 100);
  }
}

function* socialLogin({ payload, history, provider }) {
  yield put({ type: 'TOGGLE_LOGIN' });
  const { error, response } = yield call(postCall, { path: `/user/auth/${provider}`, payload });
  if (error) EventBus.publish('error', error['response']['data']['message']);
  else if (response) {
    yield put(saveloginData(response['data']['body']));
    yield put({ type: 'GET_ME' });

    // Track social login event in GTM with tracking_id
    // NOTE: User object from backend contains tracking_id (saved during signup)
    if (typeof window !== 'undefined' && window.dataLayer) {
      const eventData = {
        event: 'user_login',
        user_id: response['data']['body']?.user?._id,
        user_email: response['data']['body']?.user?.email,
        login_method: provider,
        tracking_id: response['data']['body']?.user?.tracking_id || 'none',
        acquisition_source: response['data']['body']?.user?.acquisition_source || 'direct'
      };

      // Check tracking mode: 0=all users, 1=only tracking_id users
      if (GTM_TRACK_MODE === 0 || (GTM_TRACK_MODE === 1 && eventData.tracking_id && eventData.tracking_id !== 'none')) {
        window.dataLayer.push(eventData);
      }
    }

    const workspaceId = response['data']['body']['user']?.['activeWorkspace'];
    setTimeout(() => history.push(getWorkspacePath('/agents', workspaceId)), 100);
  }
  yield put({ type: 'TOGGLE_LOGIN' });
}

function* googleLogin({ payload, history }) {
  yield call(socialLogin, { payload, history, provider: 'google' });
}

function* microsoftLogin({ payload, history }) {
  yield call(socialLogin, { payload, history, provider: 'microsoft' });
}

function* appleLogin({ payload, history }) {
  yield call(socialLogin, { payload, history, provider: 'apple' });
}

function* onSignUp({ payload, history }) {
  yield put({ type: 'TOGGLE_LOGIN' });

  // Get tracking object from localStorage
  let trackingStr = localStorage.getItem('tracking');
  let tracking = trackingStr ? JSON.parse(trackingStr) : null;

  // FALLBACK: If localStorage is empty (cross-port issue), check URL parameters
  if (!tracking && typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);

    tracking = {
      timestamp: urlParams.get('timestamp') || new Date().toISOString(),
      landingPage: urlParams.get('landingPage') || null,
      trackingId: urlParams.get('trackingId') || null,
      acquisitionSource: urlParams.get('acquisitionSource') || 'direct',
      utmSource: urlParams.get('utmSource') || null,
      utmMedium: urlParams.get('utmMedium') || null,
      utmCampaign: urlParams.get('utmCampaign') || null,
      utmContent: urlParams.get('utmContent') || null,
      utmTerm: urlParams.get('utmTerm') || null,
      fbclid: urlParams.get('fbclid') || null
    };

    // Save to localStorage for future use
    localStorage.setItem('tracking', JSON.stringify(tracking));
  }

  // Include tracking object in signup payload to backend
  const signupPayload = {
    ...payload,
    tracking: tracking
  };

  console.log('🟢 [SIGNUP]', tracking);

  const { error, response } = yield call(postCall, { path: '/user', payload: signupPayload });
  if (error) {
    EventBus.publish('error', error['response']['data']['message']);
    // if (payload['step'] === 2) setTimeout(() => history.push('/signup'), 100);
  } else if (response) {
    let { body } = response['data'];
    if (body['user']?.['emailVerified']) {
      yield put(saveloginData(body));
      yield put({ type: 'GET_ME' });

      // Track signup event (GTM + Meta)
      trackUserSignup(body?.user);

      // Check if user joined a workspace through invitation
      if (body['workspaceJoined'] && body['workspaceId']) {
        EventBus.publish('success', 'Successfully joined workspace!');
        // Redirect to the workspace they just joined
        setTimeout(() => history.push(getWorkspacePath('/agents', body['workspaceId'])), 100);
      } else {
        const workspaceId = body['user']?.['activeWorkspace'];
        setTimeout(() => history.push(getWorkspacePath('/agents', workspaceId)), 100);
      }
    } else EventBus.publish('success', response['data']['message']);
  }
  yield put({ type: 'TOGGLE_LOGIN' });
}

/*========== DASHBOARD FUNCTIONS =============*/

function* getAnalytics({ payload }) {
  const { error, response } = yield call(postCall, { path: '/user/analytics', payload });
  if (error) EventBus.publish('error', error['response']['data']['message']);
  else if (response) yield put({ type: 'SET_ANALYTICS', payload: response['data']['body'] });
}

function* resetPassword({ payload }) {
  const { formData, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: '/user/setPassword', payload: formData });
  if (error) {
    failCallback()
    EventBus.publish('error', error['response']['data']['message']);
  } else if (response) {
    successCallback();
    EventBus.publish('success', response['data']['message'])
  }
}

function* forgotPassword({ payload }) {
  const { formData, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: '/user/forgotPassword', payload: formData });
  if (error) {
    failCallback()
    EventBus.publish('error', error['response']['data']['message']);
  } else if (response) successCallback();
}

function* changePassword({ payload }) {
  const { formData, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { path: '/user/changePassword', payload: formData });
  if (error) {
    failCallback()
    EventBus.publish('error', error['response']['data']['message']);
  } else if (response) successCallback();
}

function* setSocialPassword({ payload }) {
  const { successCallback, failCallback, formData } = payload;
  const { error, response } = yield call(postCall, { path: '/user/setSocialPassword', payload: formData });
  if (error) {
    failCallback();
    EventBus.publish('error', error['response']['data']['message']);
  } else if (response) {
    successCallback();
    yield put(setUserDetails(response['data']['body']));
    EventBus.publish('success', response['data']['message']);
  }
}

function* toggle2FA({ payload }) {
  const { enable, password, successCallback, failCallback } = payload;
  const { error, response } = yield call(putCall, { path: '/user/toggle2FA', payload: { enable, password } });
  if (error) {
    if (failCallback) failCallback();
    EventBus.publish('error', error['response']['data']['message']);
  } else if (response) {
    if (successCallback) successCallback(response['data']['body']);
    EventBus.publish('success', response['data']['message']);
  }
}

function* verify2FA({ payload, history, successCallback, failCallback }) {
  const { error, response } = yield call(postCall, { path: '/user/verify2FA', payload });
  if (error) {
    if (failCallback) failCallback();
    EventBus.publish('error', error['response']['data']['message']);
  } else if (response) {
    // Save login data after successful 2FA verification
    yield put(saveloginData(response['data']['body']));
    yield put({ type: 'GET_ME' });
    if (successCallback) successCallback();
    const workspaceId = response['data']['body']['user']?.['activeWorkspace'];
    setTimeout(() => history.push(getWorkspacePath('/agents', workspaceId)), 100);
  }
}

function* getSearchResults({ payload }) {
  const { error, response } = yield call(postCall, { path: '/user/search', payload });
  if (error) EventBus.publish('error', error['response']['data']['message']);
  else if (response) yield put({ type: 'SET_SEARCH_RESPONSE', payload: response['data']['body'] });
}

function* revokeAllSessions({ payload }) {
  const { successCallback, failCallback } = payload;
  const { error } = yield call(postCall, { path: '/user/revokeAllSessions' });
  if (error) {
    failCallback();
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to revoke sessions');
  } else {
    successCallback();
  }
}

function* revokeWorkspaceSessions({ payload }) {
  const { workspaceId, successCallback, failCallback } = payload;
  const { error } = yield call(postCall, { path: `/user/workspace/${workspaceId}/revokeAllSessions` });
  if (error) {
    failCallback();
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to revoke workspace sessions');
  } else {
    successCallback();
  }
}

function* actionWatcher() {
  yield takeEvery('LOGIN', login);
  yield takeEvery('MAGIC_LOGIN_REQUEST', magicLogin);
  yield takeEvery('SIGNUP', onSignUp);
  yield takeEvery('APPLE_LOGIN', appleLogin);
  yield takeEvery('GOOGLE_LOGIN', googleLogin);
  yield takeEvery('GET_ANALYTICS', getAnalytics);
  yield takeEvery('RESET_PASSWORD', resetPassword);
  yield takeEvery('MICROSOFT_LOGIN', microsoftLogin);
  yield takeEvery('CHANGE_PASSWORD', changePassword);
  yield takeEvery('FORGOT_PASSWORD', forgotPassword);
  yield takeEvery('GET_SEARCH_RESULTS', getSearchResults);
  yield takeEvery('SET_SOCIAL_PASSWORD', setSocialPassword);
  yield takeEvery('TOGGLE_2FA', toggle2FA);
  yield takeEvery('VERIFY_2FA', verify2FA);
  yield takeEvery('REVOKE_ALL_SESSIONS', revokeAllSessions);
  yield takeEvery('REVOKE_WORKSPACE_SESSIONS', revokeWorkspaceSessions);
}

export default function* rootSaga() {
  yield all([actionWatcher()]);
}

function postCall({ path, payload }) {
  return axios
    .post(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

function getCall(path) {
  return axios
    .get(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

function deleteCall(path) {
  return axios
    .delete(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

function putCall({ path, payload }) {
  return axios
    .put(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}